/**
 * Error reporting for the websites — off until there is somewhere to send it.
 *
 * The phone reports crashes to Sentry (apps/mobile/src/lib/crash.ts). The
 * websites reported nothing: a care-home console that throws on render shows
 * a white page, and the first anyone hears of it is a phone call.
 *
 * ── How it travels ──────────────────────────────────────────────────────────
 * The page never talks to Sentry. It posts a small, already-scrubbed report
 * to its own Worker at `/_errors`, and the Worker forwards it to Sentry only
 * when that Worker has a `SENTRY_DSN` secret. So:
 *
 *  - **Off until the owner sets a DSN**, per Worker, with no rebuild. With no
 *    DSN the Worker answers 204 and drops the report; nothing leaves
 *    Cloudflare.
 *  - **The pages keep `connect-src 'self'`.** No Sentry script, no third-party
 *    origin in the CSP, and no SDK collecting breadcrumbs, URLs or form
 *    values behind our back: the only thing sent is what `buildReport`
 *    below builds, field by field.
 *  - **Sentry never sees the person's IP**: the request comes from the Worker.
 *
 * ── What it carries ─────────────────────────────────────────────────────────
 * DPDP applies and these pages show medicines, names and phone numbers, so a
 * report says what broke and where in the code, never whose page it was:
 *
 *  - the error's type, and its message only when the browser wrote it
 *    (TypeError and friends describe code) or it is an API error code like
 *    `not_found`. Any other message is our own code's, may quote what a
 *    person typed or a medicine's name, and is withheld. Every kept message
 *    is scrubbed again: URLs, emails, numbers, ids, tokens and quoted text.
 *  - stack frames: function name, the script's path, line and column. Never
 *    a query string or a hash.
 *  - the first path segment only (`/patients`), and only if it is a plain
 *    word: never a URL with an id or a token in it.
 *  - React component names from the component stack.
 *  - no user, no request, no URL, no breadcrumbs, no form contents.
 *
 * The Worker scrubs every field again before forwarding (`toSentryEvent`),
 * so a page that is out of date, or a request that never came from our page,
 * cannot send more than this.
 */

export type CrashApp = 'care' | 'org' | 'admin' | 'web';
export type CrashMechanism = 'boundary' | 'onerror' | 'unhandledrejection';

export interface CrashFrame {
  function: string | null;
  filename: string | null;
  lineno: number | null;
  colno: number | null;
}

/** What the page posts to /_errors. Nothing else is ever sent. */
export interface CrashReport {
  app: CrashApp;
  type: string;
  message: string;
  frames: CrashFrame[];
  route: string | null;
  components: string[];
  mechanism: CrashMechanism;
}

export const CRASH_ENDPOINT = '/_errors';
/** Per page load. A render loop must not send a thousand. */
export const MAX_REPORTS_PER_PAGE = 5;
/** The Worker refuses anything bigger. */
export const MAX_REPORT_BYTES = 16_384;

// ── Scrubbing ────────────────────────────────────────────────────────────────

const URL_RE = /\b[a-z][a-z0-9+.-]*:\/\/[^\s'"<>`]+/gi;
const EMAIL_RE = /[^\s@'"<>]+@[^\s@'"<>]+\.[^\s@'"<>]+/g;
const UUID_RE = /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi;
/** DrBell's own ids: `pat_0abc…` (apps/api/src/ids.ts). */
const PREFIXED_ID_RE = /\b[a-z]{2,6}_[A-Za-z0-9]{8,}\b/g;
const TOKEN_RE = /\b[A-Za-z0-9_-]{24,}\b/g;
const NUMBER_RE = /\+?\d[\d\s().-]{5,}\d/g;
const QUOTED_RE = /(["'`])(?:(?!\1).){13,}?\1/g;

/** Strip anything that could identify a person or a record from free text. */
export function scrubText(text: string, max = 300): string {
  return text
    .replace(URL_RE, '[url]')
    .replace(EMAIL_RE, '[email]')
    .replace(UUID_RE, '[id]')
    .replace(PREFIXED_ID_RE, '[id]')
    .replace(TOKEN_RE, '[token]')
    .replace(NUMBER_RE, '[number]')
    .replace(QUOTED_RE, '$1[text]$1')
    .slice(0, max);
}

/**
 * The first path segment, if it is a plain word: `/patients/pat_123/meds`
 * becomes `/patients`, `/i/Xy7…` becomes `/i`. Anything else, nothing.
 */
export function scrubRoute(pathname: string | null | undefined): string | null {
  const first = (pathname ?? '').split(/[?#]/)[0]!.split('/').filter(Boolean)[0];
  if (!first) return '/';
  return /^[a-z][a-z-]{0,23}$/.test(first) ? `/${first}` : null;
}

/** A script's location without query, hash or anything after its path's id-free part. */
export function scrubFilename(filename: string | null | undefined): string | null {
  if (!filename) return null;
  try {
    const url = new URL(filename);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return `${url.protocol}[external]`;
    // Bundles live under /assets/ and their names are content hashes.
    const path = url.pathname.startsWith('/assets/') ? url.pathname : scrubRoute(url.pathname) ?? '/[path]';
    return `${url.origin}${path}`.slice(0, 200);
  } catch {
    return /^[\w./-]{1,200}$/.test(filename) ? filename : null;
  }
}

const IDENT_RE = /^[\w$.<>\[\] /-]{1,80}$/;
const cleanFunction = (name: string | null | undefined) =>
  name && IDENT_RE.test(name) ? name : null;

// ── Messages ────────────────────────────────────────────────────────────────

/** Written by the browser, about code: safe to keep, once scrubbed. */
const ENGINE_TYPES = new Set(['TypeError', 'ReferenceError', 'RangeError', 'SyntaxError', 'URIError', 'EvalError']);

/** Browser messages for network and loading failures, which say nothing about a person. */
const BROWSER_MESSAGES = [
  /^Failed to fetch/, /^Load failed/, /^NetworkError/, /^The operation was aborted/,
  /^Failed to fetch dynamically imported module/, /^Importing a module script failed/,
  /^error loading dynamically imported module/, /^ResizeObserver loop/, /^Script error\.?$/,
];

/**
 * An API error code, as the consoles' ApiError carries: `not_found`. A bare
 * code only: anything after it (`duplicate: metformin`) could be a record's.
 */
const ERROR_CODE_RE = /^[a-z][a-z0-9_]{2,60}$/;

export function safeMessage(type: string, message: string): string {
  if (!message) return '';
  if (ENGINE_TYPES.has(type) || BROWSER_MESSAGES.some((re) => re.test(message)) || ERROR_CODE_RE.test(message)) {
    return scrubText(message);
  }
  return '[message withheld]';
}

const safeType = (type: unknown) =>
  typeof type === 'string' && /^[A-Za-z_$][\w$]{0,60}$/.test(type) ? type : 'Error';

// ── Stacks ──────────────────────────────────────────────────────────────────

/**
 * Chrome's `at fn (url:line:col)` and Firefox/Safari's `fn@url:line:col`,
 * innermost first, as the browser writes them. Lines that are neither are
 * dropped rather than guessed at.
 */
export function parseStack(stack: string | undefined): CrashFrame[] {
  if (!stack) return [];
  const frames: CrashFrame[] = [];
  for (const raw of stack.split('\n').slice(0, 40)) {
    const line = raw.trim();
    let m = /^at (?:(.+?) \()?(.+?):(\d+):(\d+)\)?$/.exec(line);
    if (!m) m = /^(.*?)@(.+?):(\d+):(\d+)$/.exec(line);
    if (!m) continue;
    frames.push({
      function: cleanFunction(m[1]?.replace(/^async /, '')),
      filename: scrubFilename(m[2]),
      lineno: Number(m[3]),
      colno: Number(m[4]),
    });
    if (frames.length >= 30) break;
  }
  return frames;
}

/** `at Medicines (url)` lines from React's component stack: names only. */
export function componentNames(componentStack: string | null | undefined): string[] {
  if (!componentStack) return [];
  const names: string[] = [];
  for (const line of componentStack.split('\n')) {
    const m = /^\s*(?:at\s+)?([A-Z][\w$.]{0,60})(?:\s|@|$)/.exec(line);
    if (m?.[1]) names.push(m[1]);
    if (names.length >= 15) break;
  }
  return names;
}

// ── The report ──────────────────────────────────────────────────────────────

export function buildReport(
  error: unknown,
  ctx: { app: CrashApp; mechanism: CrashMechanism; pathname?: string | null; componentStack?: string | null },
): CrashReport {
  const isError = error instanceof Error;
  // A thrown value that is not an Error says nothing about code; its fields
  // could be anything, so none of them are read.
  const type = isError ? safeType(error.name) : 'Error';
  const raw = isError ? error.message : typeof error === 'string' ? error : '';
  return {
    app: ctx.app,
    type,
    message: safeMessage(type, String(raw ?? '')),
    frames: parseStack(isError ? error.stack : undefined),
    route: scrubRoute(ctx.pathname),
    components: componentNames(ctx.componentStack),
    mechanism: ctx.mechanism,
  };
}

/**
 * Re-check a report that arrived at the Worker: every field typed, bounded
 * and scrubbed again. Null when it is not a report at all.
 */
export function sanitizeReport(input: unknown, app: CrashApp): CrashReport | null {
  if (!input || typeof input !== 'object') return null;
  const r = input as Record<string, unknown>;
  const mechanism = r.mechanism === 'boundary' || r.mechanism === 'onerror' || r.mechanism === 'unhandledrejection'
    ? r.mechanism : 'onerror';
  const type = safeType(r.type);
  const frames = Array.isArray(r.frames)
    ? r.frames.slice(0, 30).map((f): CrashFrame => {
        const o = (f ?? {}) as Record<string, unknown>;
        const n = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? Math.trunc(v) : null);
        return {
          function: cleanFunction(typeof o.function === 'string' ? o.function : null),
          filename: scrubFilename(typeof o.filename === 'string' ? o.filename : null),
          lineno: n(o.lineno),
          colno: n(o.colno),
        };
      })
    : [];
  return {
    app,
    type,
    message: typeof r.message === 'string'
      ? (r.message === '[message withheld]' ? r.message : safeMessage(type, r.message))
      : '',
    frames,
    route: typeof r.route === 'string' ? scrubRoute(r.route) : null,
    components: Array.isArray(r.components)
      ? r.components.filter((c): c is string => typeof c === 'string' && /^[A-Z][\w$.]{0,60}$/.test(c)).slice(0, 15)
      : [],
    mechanism,
  };
}

// ── In the page ─────────────────────────────────────────────────────────────

let config: { app: CrashApp; endpoint: string } | null = null;
let sent = 0;
const seen = new Set<string>();

/** Send one report, if reporting is set up and this page has not sent too many. */
export function reportError(error: unknown, mechanism: CrashMechanism, componentStack?: string | null): void {
  if (!config || typeof window === 'undefined') return;
  try {
    const report = buildReport(error, {
      app: config.app, mechanism, pathname: window.location.pathname, componentStack,
    });
    const key = `${report.type}|${report.message}|${report.frames[0]?.filename}:${report.frames[0]?.lineno}`;
    if (seen.has(key) || sent >= MAX_REPORTS_PER_PAGE) return;
    seen.add(key);
    sent++;
    const body = JSON.stringify(report);
    if (body.length > MAX_REPORT_BYTES) return;
    void fetch(config.endpoint, {
      method: 'POST',
      body,
      headers: { 'content-type': 'application/json' },
      // Still sent if the person reloads, which the error screen asks them to.
      keepalive: true,
      credentials: 'omit',
    }).catch(() => {});
  } catch {
    // Reporting must never be the second error.
  }
}

/**
 * Start reporting for this page: the error boundary's errors, plus anything
 * thrown outside React (`error`) and promises nobody caught. Call once,
 * before rendering.
 */
export function initErrorReporting(app: CrashApp, endpoint = CRASH_ENDPOINT): void {
  if (config || typeof window === 'undefined') return;
  config = { app, endpoint };
  window.addEventListener('error', (event) => {
    // A failed <img> or <script> fires `error` with no error object; skip it.
    if (event.error !== undefined && event.error !== null) reportError(event.error, 'onerror');
  });
  window.addEventListener('unhandledrejection', (event) => reportError(event.reason, 'unhandledrejection'));
}

/** For tests. */
export function resetErrorReporting(): void {
  config = null;
  sent = 0;
  seen.clear();
}

// ── In the Worker ───────────────────────────────────────────────────────────

interface Dsn { key: string; host: string; projectId: string; protocol: string }

/** `https://<key>@o123.ingest.sentry.io/456`. Null when it is not one. */
export function parseDsn(dsn: string | undefined): Dsn | null {
  if (!dsn) return null;
  try {
    const url = new URL(dsn.trim());
    const projectId = url.pathname.replace(/^\/+|\/+$/g, '');
    if (!url.username || !/^\d+$/.test(projectId) || (url.protocol !== 'https:' && url.protocol !== 'http:')) return null;
    return { key: url.username, host: url.host, projectId, protocol: url.protocol };
  } catch {
    return null;
  }
}

/** Chrome, Safari, Firefox or Edge — the family, never the full user agent. */
export function browserFamily(userAgent: string | null): string {
  const ua = userAgent ?? '';
  if (/Edg\//.test(ua)) return 'Edge';
  if (/Firefox\//.test(ua)) return 'Firefox';
  if (/Chrome\//.test(ua)) return 'Chrome';
  if (/Safari\//.test(ua)) return 'Safari';
  return 'Other';
}

function eventId(): string {
  return crypto.randomUUID().replace(/-/g, '');
}

/** The event Sentry stores. Built only from a sanitized report. */
export function toSentryEvent(
  report: CrashReport,
  ctx: { environment: string; browser: string; now?: number; id?: string },
) {
  return {
    event_id: ctx.id ?? eventId(),
    timestamp: (ctx.now ?? Date.now()) / 1000,
    platform: 'javascript',
    level: 'error',
    environment: ctx.environment,
    logger: `drbell-${report.app}`,
    tags: {
      app: report.app,
      mechanism: report.mechanism,
      browser: ctx.browser,
      ...(report.route ? { route: report.route } : {}),
    },
    exception: {
      values: [{
        type: report.type,
        value: report.message,
        mechanism: { type: report.mechanism, handled: report.mechanism === 'boundary' },
        // Sentry wants the outermost frame first; browsers write it last.
        stacktrace: report.frames.length
          ? { frames: [...report.frames].reverse().map((f) => ({ ...f, in_app: !!f.filename?.includes('/assets/') })) }
          : undefined,
      }],
    },
    extra: report.components.length ? { components: report.components.join(' < ') } : {},
    sdk: { name: 'drbell.crash', version: '1.0.0' },
  };
}

/** Forwards at most this many reports a minute from one Worker isolate. */
const FORWARD_PER_MINUTE = 30;
let windowStart = 0;
let forwarded = 0;

/**
 * The Worker's `/_errors` route. 204 for anything it drops, including every
 * report while there is no DSN.
 */
export async function forwardCrashReport(
  request: Request,
  opts: { dsn: string | undefined; app: CrashApp; fetcher?: typeof fetch; now?: number },
): Promise<Response> {
  const done = (status = 204) => new Response(null, { status, headers: { 'Cache-Control': 'no-store' } });
  if (request.method !== 'POST') return done(405);

  const dsn = parseDsn(opts.dsn);
  if (!dsn) return done();

  const now = opts.now ?? Date.now();
  if (now - windowStart > 60_000) {
    windowStart = now;
    forwarded = 0;
  }
  if (forwarded >= FORWARD_PER_MINUTE) return done();

  const text = await request.text().catch(() => '');
  if (!text || text.length > MAX_REPORT_BYTES) return done();
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return done();
  }
  const report = sanitizeReport(parsed, opts.app);
  if (!report) return done();

  const host = new URL(request.url).hostname;
  const event = toSentryEvent(report, {
    environment: /-dev\.|^dev\.|localhost/.test(host) ? 'staging' : 'production',
    browser: browserFamily(request.headers.get('user-agent')),
    now,
  });
  const envelope = [
    JSON.stringify({ event_id: event.event_id, sent_at: new Date(now).toISOString() }),
    JSON.stringify({ type: 'event' }),
    JSON.stringify(event),
  ].join('\n');

  forwarded++;
  try {
    await (opts.fetcher ?? fetch)(
      `${dsn.protocol}//${dsn.host}/api/${dsn.projectId}/envelope/?sentry_key=${encodeURIComponent(dsn.key)}&sentry_version=7`,
      { method: 'POST', body: envelope, headers: { 'content-type': 'application/x-sentry-envelope' } },
    );
  } catch {
    // Sentry down is not the page's problem.
  }
  return done();
}
